<template>
  <div
    class="activity self-review-activity activity-with-button"
    :class="{ mandatory: mandatory, complete: complete }"
    :activity-id="activityId"
    @click="doSelfReview"
    @keydown.enter="doSelfReview"
  >
    <div class="self-review-activity-content">
      <div class="activity-button-wrapper">
        <div class="activity-button">
          <span class="activity-button-inner">
            <transition name="fade" mode="out-in">
              <span class="complete-icon" v-if="complete" key="complete">
                <span class="glyphicon glyphicon-ok"></span>
              </span>
              <span v-else>
                <span class="glyphicon glyphicon-list"></span>
              </span>
            </transition>
          </span>
        </div>
      </div>
      <div class="activity-description">
        <slot></slot>
      </div>
    </div>
    <QuestionsModal
      v-if="associatedPlan && associatedPlan.questions"
      :questions="associatedPlan.questions"
      :pageId="pageId"
      :on-submit="selfReviewSubmit"
      :on-cancel="selfReviewCancel"
      :showModal="showModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { debug as d } from "debug";
import QuestionsModal from "../develop/questions-modal.vue";
import cms from "../../services/cms/cms";
import { Analytics } from "../../services/analytics/analytics";
import { CmsDevelopPage } from "../../services/cms/cms-type-definitions";
import { DevelopPlan } from "../../services/cms/develop-plan";

const debug = d("cs.SelfReviewActivityComponent");

const props = withDefaults(
  defineProps<{
    pageId: string;
    pageUid: string;
    mandatory: boolean;
    activityIds?: string[];
    selfReviewNumber: number;
    activityId: string;
    complete?: boolean;
    developPageDoc: CmsDevelopPage;
  }>(),
  {
    complete: false,
    activityIds: () => [],
  },
);

const activityIds = ref<string[]>(props.activityIds ?? []);

const emit = defineEmits(["self-review-completed"]);

const associatedPlan = ref<DevelopPlan | null>(null);

const showModal = ref(false);

watch(
  () => props.complete,
  () => {
    showModal.value = false;
  },
);

onMounted(async () => {
  debug("Running mounted");
  if (props.activityIds && props.mandatory) {
    activityIds.value.push(props.activityId);
  }

  const plans = await cms.getDevelopPlans();
  const plan = plans.find((p) => p.strengthItemId === props.developPageDoc.data.strength_item_id);
  associatedPlan.value = plan || null;

  debug("Finished mounted handler");
});

function doSelfReview(): void {
  if (!props.complete && !showModal.value) {
    Analytics.action("Opened Questionnaire", "QuestionnaireActions", {
      pageId: props.pageId,
      pageUid: props.pageUid,
    });
    showModal.value = true;
  }
}

function selfReviewCancel(): void {
  showModal.value = false;
  Analytics.action("Cancelled Questionnaire", "QuestionnaireActions", {
    pageId: props.pageId,
    pageUid: props.pageUid,
  });
}

function selfReviewSubmit(answers: number[]): void {
  if (!props.complete) {
    Analytics.action("Completed Questionnaire", "QuestionnaireActions", {
      pageId: props.pageId,
      pageUid: props.pageUid,
    });
    emit("self-review-completed", props.activityId, answers);
  }
}
</script>

<style lang="less">
.self-review-activity {
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 50ms;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .self-review-activity-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    user-select: none;
  }

  &:not(.complete) {
    cursor: pointer;
  }

  .activity-button {
    font-size: 48px;
    line-height: 0;
    padding: 0px;
    padding-bottom: 8px;
    display: block;
  }

  .activity-description {
    margin-left: 20px;
  }

  &.complete .complete-icon {
    color: green;
    border-color: green;
  }
}

// TODO: Move into sepearate LESS file
.activity {
  margin: 45px auto;
  padding: 30px;
  background-color: #eee;

  &:hover {
    background-color: #eaeaea;
  }
}
</style>
